/**
 * LandingPage - Página de aterrizaje ESPECTACULAR para TikTok
 * Diseño impactante tipo Terminal de Trading profesional
 */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { 
  TrendingUp, Shield, Zap, BarChart3, Bell, 
  LineChart, PieChart, Globe, Smartphone, CheckCircle,
  Play, ArrowRight, Star, Users, Clock, Target,
  Sparkles, ChevronDown, Activity, Cpu, Lock, FileDown, Check,
  Calendar, Search, Map, Calculator, FlaskConical, Brain, Database,
  Monitor, CandlestickChart, Flame, LayoutGrid, Newspaper, Radio,
  ScrollText, BookOpen, Notebook, Files, TrendingDown, Save, Scale, Wallet,
  Briefcase, Microscope, Wrench
} from 'lucide-react';
import { buildPlanTools } from '@/utils/planTools';
import LandingTerminal from './LandingTerminal';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// Paleta de MARCA KAPITAL (dorado metálico + plata del logo)
const GOLD = 'linear-gradient(135deg,#FBE9A0 0%,#E8C55C 38%,#C9A227 68%,#A67C00 100%)';
const SILVER = 'linear-gradient(135deg,#F3F5F8 0%,#C8CED6 45%,#98A0AC 100%)';

// Mapa emoji → icono lucide (acabado dorado, sustituye a los emojis de las tarjetas)
const TOOL_ICON_MAP = {
  '📊': BarChart3, '🗺️': Map, '🔍': Search, '📈': TrendingUp, '📅': Calendar,
  '💰': Wallet, '🧮': Calculator, '🧪': FlaskConical, '🧠': Brain, '🚨': Bell,
  '🗄️': Database, '🖥️': Monitor, '🕯️': CandlestickChart, '🔥': Flame, '📺': LayoutGrid,
  '📲': Smartphone, '📰': Newspaper, '📡': Radio, '📜': ScrollText, '📚': BookOpen,
  '📒': Notebook, '📑': Files, '📉': TrendingDown, '💾': Save, '🎯': Target,
  '🌍': Globe, '✨': Sparkles, '⚡': Zap, '⚖️': Scale, '🛠️': Wrench, '⚙️': Cpu,
};
const ToolIcon = ({ emoji, className = '' }) => {
  const Ico = TOOL_ICON_MAP[emoji] || Activity;
  return <Ico className={className} style={{ color: '#E8C55C' }} strokeWidth={2} />;
};

const LandingPage = ({ onGetStarted }) => {
  const [mounted, setMounted] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const heroRef = useRef(null);
  const planTools = useMemo(() => buildPlanTools(), []);
  // Recuentos reales (fuente única: WINDOWS_CONFIG vía planTools) para unificar
  // TODAS las cifras de "herramientas" que se muestran en la landing/login.
  const totalTools = planTools.ultimate.length;
  const freeToolsCount = planTools.free.length;
  const proToolsCount = planTools.pro.length;

  // Descarga PDF con TODAS las herramientas por plan (mismo formato que PricingPage)
  const handleDownloadPDF = () => {
    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();

    doc.setFillColor(10, 22, 40);
    doc.rect(0, 0, pageWidth, 120, 'F');
    doc.setTextColor(255, 214, 0);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(26);
    doc.text('KAPITAL · Herramientas por plan', pageWidth / 2, 55, { align: 'center' });
    doc.setTextColor(180, 200, 220);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.text('Comparativa completa · Gratis · Pro (€19.99) · Ultimate (€29.99)', pageWidth / 2, 82, { align: 'center' });
    doc.setFontSize(9);
    doc.text(`Generado el ${new Date().toLocaleString('es-ES', { timeZone: 'Europe/Madrid' })}`, pageWidth / 2, 100, { align: 'center' });

    const plansForPdf = [
      { id: 'free', name: 'Gratis', priceLabel: 'Gratis', target: 'Para explorar la plataforma', tools: planTools.free, color: [100, 116, 139] },
      { id: 'pro', name: 'Pro', priceLabel: '€19.99', target: 'Ideal para principiantes', tools: planTools.pro, color: [6, 182, 212] },
      { id: 'ultimate', name: 'Ultimate', priceLabel: '€29.99', target: 'Para traders con experiencia', tools: planTools.ultimate, color: [245, 158, 11] },
    ];

    autoTable(doc, {
      startY: 140,
      head: [['Plan', 'Precio', 'Total', 'Recomendado para']],
      body: plansForPdf.map(p => [p.name, p.priceLabel + ' (pago único)', `${p.tools.length} herramientas`, p.target]),
      theme: 'grid',
      headStyles: { fillColor: [15, 118, 176], textColor: 255, fontStyle: 'bold' },
      styles: { fontSize: 10, cellPadding: 6 },
      alternateRowStyles: { fillColor: [246, 250, 253] },
    });

    plansForPdf.forEach(p => {
      doc.addPage();
      doc.setFillColor(...p.color);
      doc.rect(0, 0, pageWidth, 70, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(22);
      doc.text(`Plan ${p.name} — ${p.priceLabel}`, 40, 35);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11);
      doc.text(`${p.target} · ${p.tools.length} herramientas incluidas`, 40, 55);

      autoTable(doc, {
        startY: 90,
        head: [['#', 'Herramienta', 'Descripción']],
        body: p.tools.map((t, idx) => [String(idx + 1), t.name, t.description || '—']),
        theme: 'grid',
        headStyles: { fillColor: p.color, textColor: 255, fontStyle: 'bold' },
        styles: { fontSize: 9.5, cellPadding: 5 },
        alternateRowStyles: { fillColor: [246, 250, 253] },
        columnStyles: {
          0: { cellWidth: 30, halign: 'center' },
          1: { cellWidth: 180, fontStyle: 'bold' },
        },
      });
    });

    doc.save(`KAPITAL_planes_herramientas_${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  // Descarga PDF de UN plan concreto (id: 'free' | 'pro' | 'ultimate')
  const handleDownloadPlanPDF = (planId) => {
    const plansMap = {
      free: { name: 'Gratis', priceLabel: 'Gratis', target: 'Para explorar la plataforma', tools: planTools.free, color: [100, 116, 139] },
      pro: { name: 'Pro', priceLabel: '€19.99', target: 'Ideal para principiantes', tools: planTools.pro, color: [6, 182, 212] },
      ultimate: { name: 'Ultimate', priceLabel: '€29.99', target: 'Para traders con experiencia', tools: planTools.ultimate, color: [245, 158, 11] },
    };
    const p = plansMap[planId];
    if (!p) return;

    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();

    // Portada
    doc.setFillColor(...p.color);
    doc.rect(0, 0, pageWidth, 130, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(28);
    doc.text(`KAPITAL · Plan ${p.name}`, pageWidth / 2, 60, { align: 'center' });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(14);
    doc.text(`${p.priceLabel} · Pago único, sin suscripciones`, pageWidth / 2, 88, { align: 'center' });
    doc.setFontSize(11);
    doc.text(p.target, pageWidth / 2, 108, { align: 'center' });

    doc.setTextColor(80, 90, 100);
    doc.setFontSize(9);
    doc.text(`Generado el ${new Date().toLocaleString('es-ES', { timeZone: 'Europe/Madrid' })}`, pageWidth / 2, 150, { align: 'center' });

    // Resumen del plan
    doc.setTextColor(20, 30, 50);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.text(`Este plan incluye ${p.tools.length} herramientas`, 40, 185);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(90, 100, 120);
    doc.text('A continuación tienes el listado completo con descripción de cada herramienta.', 40, 202);

    autoTable(doc, {
      startY: 220,
      head: [['#', 'Herramienta', 'Descripción']],
      body: p.tools.map((t, idx) => [String(idx + 1), t.name, t.description || '—']),
      theme: 'grid',
      headStyles: { fillColor: p.color, textColor: 255, fontStyle: 'bold' },
      styles: { fontSize: 9.5, cellPadding: 6 },
      alternateRowStyles: { fillColor: [246, 250, 253] },
      columnStyles: {
        0: { cellWidth: 30, halign: 'center' },
        1: { cellWidth: 180, fontStyle: 'bold' },
      },
    });

    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150, 160, 170);
      doc.text(`KAPITAL · Plan ${p.name} · Página ${i}/${pageCount}`, pageWidth / 2, doc.internal.pageSize.getHeight() - 20, { align: 'center' });
    }

    doc.save(`KAPITAL_plan_${planId}_${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  useEffect(() => {
    setMounted(true);
  }, []);

  // Parallax mouse effect
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (heroRef.current) {
        const rect = heroRef.current.getBoundingClientRect();
        const x = (e.clientX - rect.left - rect.width / 2) / 50;
        const y = (e.clientY - rect.top - rect.height / 2) / 50;
        setMousePosition({ x, y });
      }
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Animated counter
  const AnimatedNumber = ({ value, suffix = '' }) => {
    const [count, setCount] = useState(0);
    useEffect(() => {
      if (!mounted) return;
      const target = parseInt(value);
      const duration = 2000;
      const increment = target / (duration / 16);
      let current = 0;
      const timer = setInterval(() => {
        current += increment;
        if (current >= target) {
          setCount(target);
          clearInterval(timer);
        } else {
          setCount(Math.floor(current));
        }
      }, 16);
      return () => clearInterval(timer);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);
    return <span>{count}{suffix}</span>;
  };

  // Floating particles
  const FloatingParticles = () => (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {[...Array(20)].map((_, i) => (
        <div
          key={i}
          className="absolute w-1 h-1 bg-gold-300/30 rounded-full"
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animation: `float-particle ${5 + Math.random() * 10}s ease-in-out infinite`,
            animationDelay: `${Math.random() * 5}s`,
          }}
        />
      ))}
    </div>
  );

  // Live ticker simulation
  const LiveTicker = () => {
    const tickers = [
      { symbol: 'AAPL', price: '174.50', change: '+2.34%', up: true },
      { symbol: 'TSLA', price: '228.75', change: '+1.82%', up: true },
      { symbol: 'NVDA', price: '805.20', change: '+4.12%', up: true },
      { symbol: 'META', price: '446.80', change: '-0.45%', up: false },
      { symbol: 'AMZN', price: '164.10', change: '+1.23%', up: true },
      { symbol: 'BTC', price: '62.050', change: '+3.21%', up: true },
      { symbol: 'ETH', price: '3.240', change: '+2.87%', up: true },
      { symbol: 'GOOGL', price: '130.50', change: '+0.98%', up: true },
    ];

    return (
      <div className="absolute top-0 left-0 right-0 overflow-hidden bg-black/40 backdrop-blur-sm border-b border-gold-500/20 z-20">
        <div className="flex animate-ticker">
          {[...tickers, ...tickers].map((t, i) => (
            <div key={i} className="flex items-center gap-3 px-6 py-2 border-r border-white/5 whitespace-nowrap">
              <span className="text-white font-mono font-bold text-sm">{t.symbol}</span>
              <span className="text-slate-400 font-mono text-sm">{t.price}€</span>
              <span className={`font-mono text-sm font-semibold ${t.up ? 'text-emerald-400' : 'text-red-400'}`}>
                {t.change}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#030712] text-white overflow-x-hidden">
      {/* CSS Animations */}
      <style>{`
        @keyframes float-particle {
          0%, 100% { transform: translateY(0) translateX(0); opacity: 0.3; }
          25% { transform: translateY(-20px) translateX(10px); opacity: 0.6; }
          50% { transform: translateY(-40px) translateX(-10px); opacity: 0.3; }
          75% { transform: translateY(-20px) translateX(15px); opacity: 0.6; }
        }
        @keyframes ticker {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-ticker {
          animation: ticker 30s linear infinite;
        }
        @keyframes pulse-glow {
          0%, 100% { box-shadow: 0 0 20px rgba(6, 182, 212, 0.3), 0 0 60px rgba(6, 182, 212, 0.1); }
          50% { box-shadow: 0 0 40px rgba(6, 182, 212, 0.5), 0 0 80px rgba(6, 182, 212, 0.2); }
        }
        @keyframes gradient-shift {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-20px); }
        }
        @keyframes scan-line {
          0% { transform: translateY(-100%); }
          100% { transform: translateY(100vh); }
        }
        .animate-float { animation: float 6s ease-in-out infinite; }
        .animate-pulse-glow { animation: pulse-glow 3s ease-in-out infinite; }
        .gradient-text {
          background: linear-gradient(135deg, #06b6d4 0%, #00ff88 25%, #06b6d4 50%, #a855f7 75%, #06b6d4 100%);
          background-size: 200% auto;
          animation: gradient-shift 4s ease infinite;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .card-glow:hover {
          box-shadow: 0 0 30px rgba(6, 182, 212, 0.2), inset 0 1px 0 rgba(255,255,255,0.1);
        }
      `}</style>

      {/* NUEVO: Terminal financiero público estilo Investing/DEGIRO */}
      <LandingTerminal onGetStarted={onGetStarted} />

      {/* Hero heredado (desactivado, se conserva por referencia) */}
      {false && (
      <section 
        ref={heroRef}
        className="min-h-screen w-full flex flex-col items-center justify-start pt-12 text-center px-4 relative overflow-hidden"
      >
        {/* Animated Background */}
        <div className="absolute inset-0">
          {/* Deep gradient base */}
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-[#030712] to-slate-950" />
          
          {/* Animated grid */}
          <div 
            className="absolute inset-0 opacity-[0.07]"
            style={{
              backgroundImage: `
                linear-gradient(rgba(6, 182, 212, 0.5) 1px, transparent 1px),
                linear-gradient(90deg, rgba(6, 182, 212, 0.5) 1px, transparent 1px)
              `,
              backgroundSize: '80px 80px',
              transform: `translate(${mousePosition.x}px, ${mousePosition.y}px)`,
              transition: 'transform 0.3s ease-out',
            }}
          />

          {/* Radial glow behind logo */}
          <div 
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full blur-[150px] opacity-20"
            style={{ 
              background: 'radial-gradient(circle, #06b6d4 0%, transparent 60%)',
              transform: `translate(calc(-50% + ${mousePosition.x * 2}px), calc(-50% + ${mousePosition.y * 2}px))`,
            }}
          />

          {/* Accent orbs */}
          <div 
            className="absolute top-20 right-20 w-[400px] h-[400px] rounded-full blur-[120px] opacity-10 animate-float"
            style={{ background: 'radial-gradient(circle, #00ff88 0%, transparent 70%)' }}
          />
          <div 
            className="absolute bottom-20 left-20 w-[300px] h-[300px] rounded-full blur-[100px] opacity-10 animate-float"
            style={{ background: 'radial-gradient(circle, #a855f7 0%, transparent 70%)', animationDelay: '2s' }}
          />

          {/* Scan line effect */}
          <div 
            className="absolute left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-gold-300/30 to-transparent"
            style={{ animation: 'scan-line 8s linear infinite' }}
          />
        </div>

        <FloatingParticles />

        {/* Content - TODO GRANDE Y COMPACTO */}
        <div className="relative z-10 w-full max-w-7xl mx-auto flex flex-col items-center px-8">
          {/* NUEVO LOGO - Más grande */}
          <img 
            src="/logo-kapital-at.png" 
            alt="Análisis Técnico KAPITAL" 
            className={`w-full max-w-3xl lg:max-w-4xl xl:max-w-5xl h-auto object-contain transition-all duration-1000 ${mounted ? 'opacity-100 scale-100' : 'opacity-0 scale-90'}`}
            style={{
              filter: 'drop-shadow(0 0 60px rgba(212, 175, 55, 0.4))',
              marginBottom: '-2rem',
              mixBlendMode: 'lighten',
            }}
            data-testid="hero-kapital-logo"
          />

          {/* Tagline */}
          <p 
            className={`text-xl md:text-2xl lg:text-3xl font-light tracking-wide mb-5 transition-all duration-700 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
            style={{ transitionDelay: '0.1s' }}
          >
            <span className="text-white">Tu </span>
            <span className="font-bold text-transparent bg-clip-text" style={{ backgroundImage: 'linear-gradient(90deg, #FFD700, #FFA500)' }}>Terminal de Trading</span>
            <span className="text-white"> Profesional</span>
          </p>

          {/* Características - MÁS GRANDES y espaciadas */}
          <div 
            className={`flex flex-wrap justify-center gap-4 md:gap-6 mb-6 transition-all duration-700 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ transitionDelay: '0.2s' }}
          >
            <div className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-emerald-500/15 border border-emerald-500/40">
              <div className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-emerald-400 font-bold text-sm md:text-base">Tiempo Real</span>
            </div>
            <div className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-gold-500/15 border border-gold-500/40">
              <Bell className="w-4 h-4 text-gold-400" />
              <span className="text-gold-400 font-bold text-sm md:text-base">Alertas</span>
            </div>
            <div className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-yellow-500/15 border border-yellow-500/40">
              <BarChart3 className="w-4 h-4 text-yellow-200" />
              <span className="text-yellow-200 font-bold text-sm md:text-base">{totalTools} Tools</span>
            </div>
            <div className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-rose-500/15 border border-rose-500/40">
              <Globe className="w-4 h-4 text-rose-400" />
              <span className="text-rose-400 font-bold text-sm md:text-base">Global</span>
            </div>
          </div>

          {/* CTA Buttons - Más grandes */}
          <div 
            className={`flex flex-col sm:flex-row items-center justify-center gap-4 mb-6 transition-all duration-700 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ transitionDelay: '0.3s' }}
          >
            <button
              onClick={onGetStarted}
              className="group relative h-14 px-10 rounded-xl font-bold text-lg transition-all duration-300 flex items-center gap-3 overflow-hidden text-slate-900"
              style={{
                background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 50%, #FF6B35 100%)',
                boxShadow: '0 0 40px rgba(255, 165, 0, 0.4), 0 4px 20px rgba(0,0,0,0.3)',
              }}
              data-testid="hero-cta-primary"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
              <span className="relative font-black">EMPEZAR AHORA</span>
              <ArrowRight className="w-6 h-6 relative group-hover:translate-x-2 transition-transform" />
            </button>
            <button
              onClick={() => document.getElementById('features').scrollIntoView({ behavior: 'smooth' })}
              className="group h-14 px-10 rounded-xl border-2 border-white/30 bg-white/5 backdrop-blur-sm text-white font-bold text-lg hover:bg-white/10 hover:border-white/50 transition-all duration-300 flex items-center gap-3"
              data-testid="hero-cta-secondary"
            >
              <Play className="w-5 h-5" />
              Ver Funciones
            </button>
          </div>

          {/* Stats más grandes y espaciados */}
          <div 
            className={`flex flex-wrap justify-center gap-12 md:gap-20 lg:gap-28 mb-6 transition-all duration-700 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ transitionDelay: '0.4s' }}
          >
            <div className="text-center">
              <div className="text-3xl md:text-4xl lg:text-5xl font-black mb-1" style={{ color: '#00ff88' }}>
                <AnimatedNumber value="10000" suffix="+" />
              </div>
              <div className="text-xs md:text-sm text-slate-400 uppercase tracking-wider font-medium">Activos</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl lg:text-5xl font-black mb-1" style={{ color: '#FFD700' }}>
                <AnimatedNumber value={String(totalTools)} suffix="" />
              </div>
              <div className="text-xs md:text-sm text-slate-400 uppercase tracking-wider font-medium">Herramientas</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl lg:text-5xl font-black mb-1" style={{ color: '#FF6B35' }}>24/7</div>
              <div className="text-xs md:text-sm text-slate-400 uppercase tracking-wider font-medium">Tiempo Real</div>
            </div>
          </div>

          {/* Trust Bar - Más grande y espaciado */}
          <div 
            className={`flex flex-wrap justify-center items-center gap-6 md:gap-10 lg:gap-12 mt-4 transition-all duration-700 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ transitionDelay: '0.5s' }}
          >
            {[
              { icon: Shield, text: 'Datos Encriptados', color: '#06b6d4' },
              { icon: Zap, text: 'Sin Anuncios', color: '#fbbf24' },
              { icon: Smartphone, text: 'Móvil + Desktop', color: '#a855f7' },
              { icon: Globe, text: 'Mercados Globales', color: '#10b981' },
              { icon: Lock, text: '100% Privado', color: '#3b82f6' },
            ].map((item, idx) => (
              <div key={idx} className="flex items-center gap-2 text-slate-400">
                <item.icon className="w-4 h-4" style={{ color: item.color }} />
                <span className="text-sm font-medium">{item.text}</span>
              </div>
            ))}
          </div>

        </div>
      </section>
      )}

      {/* Features Section - HERRAMIENTAS POR GRUPOS */}
      <section id="features" className="py-20 px-4 relative overflow-hidden">
        {/* Background effects */}
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950" />
        <div className="absolute top-0 left-0 w-full h-full">
          <div className="absolute top-20 left-1/4 w-[500px] h-[500px] rounded-full blur-[150px] opacity-20 bg-gold-500" />
          <div className="absolute bottom-20 right-1/4 w-[400px] h-[400px] rounded-full blur-[150px] opacity-15 bg-yellow-500" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full blur-[200px] opacity-10 bg-gold-500" />
        </div>
        
        <div className="max-w-7xl mx-auto relative">
          {/* Header espectacular */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full border border-gold-400/30 mb-6" style={{ background: 'linear-gradient(90deg, rgba(201,162,39,0.12), rgba(232,197,92,0.18), rgba(201,162,39,0.12))', boxShadow: '0 0 30px rgba(201,162,39,0.18)' }}>
              <span className="w-2 h-2 rounded-full bg-gold-400 animate-pulse" />
              <span className="font-black tracking-wide text-transparent bg-clip-text" style={{ backgroundImage: GOLD }}>{totalTools} HERRAMIENTAS PROFESIONALES</span>
              <span className="w-2 h-2 rounded-full bg-gold-400 animate-pulse" />
            </div>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black mb-6 leading-tight tracking-tight">
              Tu <span className="text-transparent bg-clip-text" style={{ backgroundImage: GOLD }}>Arsenal Completo</span>
              <br />
              <span className="text-3xl sm:text-4xl lg:text-5xl text-slate-300">para Dominar el Mercado</span>
            </h2>
            <p className="text-slate-400 max-w-3xl mx-auto text-lg leading-relaxed">
              Cada herramienta diseñada para darte <span className="text-gold-300 font-semibold">ventaja competitiva</span>. 
              Desde análisis técnico hasta automatización total.
            </p>
          </div>

          {/* Stats impactantes */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-16 max-w-4xl mx-auto">
            {[
              { num: String(totalTools), label: 'Herramientas', icon: '🛠️', color: 'amber' },
              { num: '20+', label: 'Indicadores', icon: '📊', color: 'amber' },
              { num: '10K+', label: 'Activos', icon: '🌍', color: 'amber' },
              { num: '24/7', label: 'Tiempo Real', icon: '⚡', color: 'amber' }
            ].map((stat, i) => (
              <div key={i} className={`text-center p-4 rounded-2xl bg-gradient-to-b from-slate-800/80 to-slate-900/50 border border-${stat.color}-500/20 hover:border-${stat.color}-500/40 transition-all hover:scale-105`}>
                <div className="flex justify-center mb-2"><ToolIcon emoji={stat.icon} className="w-7 h-7" /></div>
                <div className="text-3xl font-black text-white">{stat.num}</div>
                <div className="text-xs text-slate-400 uppercase tracking-wider">{stat.label}</div>
              </div>
            ))}
          </div>

          {/* GRUPOS DE HERRAMIENTAS */}
          <div className="space-y-8">
            
            {/* Helper para renderizar badges acumulativos */}
            {(() => {
              const PlanBadges = ({ plan }) => {
                // GRATIS incluye: GRATIS + PRO + ULTIMATE (3 badges)
                // PRO incluye: PRO + ULTIMATE (2 badges)
                // ULTIMATE incluye: solo ULTIMATE (1 badge)
                const badges = [];
                if (plan === 'GRATIS') {
                  badges.push({ label: 'G', color: 'bg-slate-500/25 text-slate-300 border-slate-400/40' });
                  badges.push({ label: 'P', color: 'bg-zinc-300/15 text-zinc-200 border-zinc-300/40' });
                  badges.push({ label: 'U', color: 'bg-gold-400/25 text-gold-300 border-gold-400/55' });
                } else if (plan === 'PRO') {
                  badges.push({ label: 'P', color: 'bg-zinc-300/15 text-zinc-200 border-zinc-300/40' });
                  badges.push({ label: 'U', color: 'bg-gold-400/25 text-gold-300 border-gold-400/55' });
                } else {
                  badges.push({ label: 'U', color: 'bg-gold-400/25 text-gold-300 border-gold-400/55' });
                }
                return (
                  <div className="flex gap-0.5">
                    {badges.map((b, i) => (
                      <span key={i} className={`text-[8px] w-4 h-4 flex items-center justify-center rounded border font-black ${b.color}`}>
                        {b.label}
                      </span>
                    ))}
                  </div>
                );
              };
              
              return (
                <>
            {/* GRUPO 1: ANÁLISIS TÉCNICO */}
            <div className="relative">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-gold-500/30 to-gold-600/10 flex items-center justify-center border border-gold-500/30">
                  <TrendingUp className="w-5 h-5" style={{ color: '#E8C55C' }} strokeWidth={2} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Análisis Técnico</h3>
                  <p className="text-xs text-gold-300">Domina los gráficos como un profesional</p>
                </div>
                <div className="flex-1 h-px bg-gradient-to-r from-gold-500/50 to-transparent ml-4" />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                {[
                  { name: 'Mapa Velas Ampliado', icon: '🕯️', plan: 'PRO' },
                  { name: 'Análisis Indicadores', icon: '📉', plan: 'PRO' },
                  { name: 'Análisis Técnico Pro', icon: '📊', plan: 'PRO' },
                  { name: 'Análisis Combinado', icon: '🧠', plan: 'PRO' },
                  { name: 'Señales Trading', icon: '⚡', plan: 'PRO' },
                  { name: 'Multi-Gráfico', icon: '📺', plan: 'ULTIMATE' },
                ].map((tool, i) => (
                  <div key={i} className="group p-2 sm:p-3 rounded-xl bg-slate-800/50 border border-slate-700/50 hover:border-gold-500/50 hover:bg-slate-800/80 transition-all cursor-pointer min-h-[80px]">
                    <div className="flex items-start justify-between mb-1 sm:mb-2">
                      <ToolIcon emoji={tool.icon} className="w-5 h-5 sm:w-6 sm:h-6 group-hover:scale-110 transition-transform" />
                      <PlanBadges plan={tool.plan} />
                    </div>
                    <p className="text-[10px] sm:text-xs font-medium text-slate-300 group-hover:text-white transition-colors leading-tight">{tool.name}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* GRUPO 2: GESTIÓN DE CARTERA */}
            <div className="relative">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-gold-500/30 to-gold-600/10 flex items-center justify-center border border-gold-500/30">
                  <Briefcase className="w-5 h-5" style={{ color: '#E8C55C' }} strokeWidth={2} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Gestión de Cartera</h3>
                  <p className="text-xs text-gold-300">Controla tu dinero en tiempo real</p>
                </div>
                <div className="flex-1 h-px bg-gradient-to-r from-gold-500/50 to-transparent ml-4" />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                {[
                  { name: 'Mi Portafolio', icon: '💰', plan: 'GRATIS' },
                  { name: 'Cartera en Vivo', icon: '📡', plan: 'PRO' },
                  { name: 'Cartera Detallada', icon: '🖥️', plan: 'PRO' },
                  { name: 'Contabilidad', icon: '📒', plan: 'ULTIMATE' },
                  { name: 'Análisis Rendimiento', icon: '📈', plan: 'PRO' },
                  { name: 'Análisis Heatmap', icon: '🔥', plan: 'ULTIMATE' },
                ].map((tool, i) => (
                  <div key={i} className="group p-3 rounded-xl bg-slate-800/50 border border-slate-700/50 hover:border-gold-500/50 hover:bg-slate-800/80 transition-all cursor-pointer">
                    <div className="flex items-start justify-between mb-2">
                      <ToolIcon emoji={tool.icon} className="w-6 h-6 group-hover:scale-110 transition-transform" />
                      <PlanBadges plan={tool.plan} />
                    </div>
                    <p className="text-xs font-medium text-slate-300 group-hover:text-white transition-colors">{tool.name}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* GRUPO 3: INVESTIGACIÓN Y DATOS */}
            <div className="relative">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-yellow-500/30 to-yellow-600/10 flex items-center justify-center border border-yellow-500/30">
                  <Microscope className="w-5 h-5" style={{ color: '#E8C55C' }} strokeWidth={2} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Investigación y Datos</h3>
                  <p className="text-xs text-yellow-200">Encuentra las mejores oportunidades</p>
                </div>
                <div className="flex-1 h-px bg-gradient-to-r from-yellow-500/50 to-transparent ml-4" />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                {[
                  { name: 'Buscador Acciones', icon: '🔍', plan: 'PRO' },
                  { name: 'Comparador Acciones', icon: '⚖️', plan: 'GRATIS' },
                  { name: 'Datos Financieros', icon: '📑', plan: 'PRO' },
                  { name: 'Base de Datos', icon: '🗄️', plan: 'PRO' },
                  { name: 'Análisis Correlación', icon: '🗺️', plan: 'PRO' },
                  { name: 'Analista Pro', icon: '✨', plan: 'ULTIMATE' },
                ].map((tool, i) => (
                  <div key={i} className="group p-3 rounded-xl bg-slate-800/50 border border-slate-700/50 hover:border-yellow-500/50 hover:bg-slate-800/80 transition-all cursor-pointer">
                    <div className="flex items-start justify-between mb-2">
                      <ToolIcon emoji={tool.icon} className="w-6 h-6 group-hover:scale-110 transition-transform" />
                      <PlanBadges plan={tool.plan} />
                    </div>
                    <p className="text-xs font-medium text-slate-300 group-hover:text-white transition-colors">{tool.name}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* GRUPO 4: ALERTAS Y NOTICIAS */}
            <div className="relative">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-gold-500/30 to-gold-600/10 flex items-center justify-center border border-gold-500/30">
                  <Bell className="w-5 h-5" style={{ color: '#E8C55C' }} strokeWidth={2} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Alertas y Noticias</h3>
                  <p className="text-xs text-gold-400">Nunca te pierdas una oportunidad</p>
                </div>
                <div className="flex-1 h-px bg-gradient-to-r from-gold-500/50 to-transparent ml-4" />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                {[
                  { name: 'Alertas de Precio', icon: '🚨', plan: 'GRATIS' },
                  { name: 'Notificaciones', icon: '📲', plan: 'PRO' },
                  { name: 'Noticias', icon: '📰', plan: 'PRO' },
                  { name: 'Calendario Económico', icon: '📅', plan: 'PRO' },
                  { name: 'Gestor Dividendos', icon: '💰', plan: 'PRO' },
                  { name: 'Datos Históricos', icon: '📜', plan: 'PRO' },
                ].map((tool, i) => (
                  <div key={i} className="group p-3 rounded-xl bg-slate-800/50 border border-slate-700/50 hover:border-gold-500/50 hover:bg-slate-800/80 transition-all cursor-pointer">
                    <div className="flex items-start justify-between mb-2">
                      <ToolIcon emoji={tool.icon} className="w-6 h-6 group-hover:scale-110 transition-transform" />
                      <PlanBadges plan={tool.plan} />
                    </div>
                    <p className="text-xs font-medium text-slate-300 group-hover:text-white transition-colors">{tool.name}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* GRUPO 5: ESTRATEGIA Y SIMULACIÓN */}
            <div className="relative">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-gold-500/30 to-gold-600/10 flex items-center justify-center border border-gold-500/30">
                  <Target className="w-5 h-5" style={{ color: '#E8C55C' }} strokeWidth={2} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Estrategia y Simulación</h3>
                  <p className="text-xs text-gold-200">Prueba antes de arriesgar tu dinero</p>
                </div>
                <div className="flex-1 h-px bg-gradient-to-r from-gold-500/50 to-transparent ml-4" />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                {[
                  { name: 'Backtesting', icon: '🧪', plan: 'PRO' },
                  { name: 'Calculadoras', icon: '🧮', plan: 'PRO' },
                  { name: 'Estrategia Compra', icon: '📅', plan: 'PRO' },
                  { name: 'Simulador Inversiones', icon: '📈', plan: 'ULTIMATE' },
                  { name: 'Objetivos Financieros', icon: '🎯', plan: 'ULTIMATE' },
                  { name: 'Análisis EBITDA', icon: '📊', plan: 'ULTIMATE' },
                ].map((tool, i) => (
                  <div key={i} className="group p-3 rounded-xl bg-slate-800/50 border border-slate-700/50 hover:border-gold-500/50 hover:bg-slate-800/80 transition-all cursor-pointer">
                    <div className="flex items-start justify-between mb-2">
                      <ToolIcon emoji={tool.icon} className="w-6 h-6 group-hover:scale-110 transition-transform" />
                      <PlanBadges plan={tool.plan} />
                    </div>
                    <p className="text-xs font-medium text-slate-300 group-hover:text-white transition-colors">{tool.name}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* GRUPO 6: UTILIDADES */}
            <div className="relative">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-slate-500/30 to-slate-600/10 flex items-center justify-center border border-slate-500/30">
                  <Cpu className="w-5 h-5" style={{ color: '#E8C55C' }} strokeWidth={2} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Utilidades</h3>
                  <p className="text-xs text-slate-400">Herramientas que completan tu experiencia</p>
                </div>
                <div className="flex-1 h-px bg-gradient-to-r from-slate-500/50 to-transparent ml-4" />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                {[
                  { name: 'Mapa Finviz', icon: '🗺️', plan: 'PRO' },
                  { name: 'Comparador Índices', icon: '🌍', plan: 'PRO' },
                  { name: 'Historial Máx/Mín', icon: '📊', plan: 'PRO' },
                  { name: 'Backup', icon: '💾', plan: 'PRO' },
                  { name: 'Analizador Activos', icon: '🔍', plan: 'PRO' },
                  { name: 'Manual', icon: '📚', plan: 'GRATIS' },
                ].map((tool, i) => (
                  <div key={i} className="group p-3 rounded-xl bg-slate-800/50 border border-slate-700/50 hover:border-slate-500/50 hover:bg-slate-800/80 transition-all cursor-pointer">
                    <div className="flex items-start justify-between mb-2">
                      <ToolIcon emoji={tool.icon} className="w-6 h-6 group-hover:scale-110 transition-transform" />
                      <PlanBadges plan={tool.plan} />
                    </div>
                    <p className="text-xs font-medium text-slate-300 group-hover:text-white transition-colors">{tool.name}</p>
                  </div>
                ))}
              </div>
            </div>
                </>
              );
            })()}

          </div>

          {/* Resumen y CTA */}
          <div className="mt-16 text-center">
            <div className="inline-flex flex-wrap justify-center gap-4 mb-8">
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/30">
                <span className="w-3 h-3 rounded-full bg-emerald-400"></span>
                <span className="text-emerald-400 font-medium text-sm">{freeToolsCount} herramientas GRATIS</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-zinc-300/10 border border-zinc-300/30">
                <span className="w-3 h-3 rounded-full bg-zinc-200"></span>
                <span className="text-zinc-200 font-medium text-sm">{proToolsCount} herramientas PRO</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-gold-500/10 border border-gold-400/40">
                <span className="w-3 h-3 rounded-full bg-gold-400"></span>
                <span className="text-gold-300 font-medium text-sm">{totalTools} herramientas ULTIMATE</span>
              </div>
            </div>
            <button 
              onClick={onGetStarted}
              className="inline-flex items-center gap-3 px-10 py-5 rounded-2xl font-black text-lg text-slate-900 transition-transform duration-300 hover:scale-105"
              style={{ background: GOLD, boxShadow: '0 0 40px rgba(201,162,39,0.35)' }}
            >
              <span>Comenzar Ahora</span>
              <ArrowRight className="w-6 h-6" />
            </button>
            <p className="mt-4 text-slate-500 text-sm">Empieza gratis · Sin tarjeta de crédito</p>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="planes" className="py-24 px-4 relative overflow-hidden">
        {/* Background effects */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-yellow-500/5 to-transparent" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[600px] rounded-full blur-[200px] opacity-10 bg-gold-500" />
        
        <div className="max-w-6xl mx-auto relative">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1 rounded-full bg-gold-500/10 border border-gold-500/20 text-gold-400 text-sm font-medium mb-4">
              💎 INVERSIÓN ÚNICA
            </span>
            <h2 className="text-4xl sm:text-5xl font-black mb-4">
              Paga <span className="text-gold-400">Una Vez</span>. Usa <span className="text-gold-300">Para Siempre</span>.
            </h2>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">
              Sin suscripciones mensuales que drenan tu bolsillo. Una inversión inteligente que se paga sola.
            </p>
            {/* Descargar comparativa en PDF */}
            <button
              onClick={handleDownloadPDF}
              className="inline-flex items-center gap-2 mt-5 px-4 py-2 rounded-full bg-gradient-to-r from-gold-500/20 to-orange-500/20 border border-gold-500/40 text-gold-300 hover:bg-zinc-800/70 hover:border-gold-400 transition-all"
              data-testid="landing-download-pdf-btn"
              title="Descarga un PDF con TODAS las herramientas de cada plan"
            >
              <FileDown size={16} strokeWidth={2.5} />
              <span className="font-medium text-sm">Descargar comparativa (PDF)</span>
            </button>
          </div>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {/* Free */}
            <div className="group p-6 bg-slate-900/50 border border-slate-700/50 rounded-2xl hover:border-slate-600/50 transition-all duration-300 relative">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-slate-600 to-slate-500 rounded-t-2xl opacity-50" />
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 rounded-xl bg-slate-800 flex items-center justify-center">
                    <span className="text-2xl">🆓</span>
                  </div>
                  <div>
                    <span className="text-slate-500 text-xs uppercase tracking-wider">Plan</span>
                    <div className="text-xl font-bold text-white">Explorador</div>
                  </div>
                </div>
                <div className="text-4xl font-black text-white mt-4">Gratis</div>
                <p className="text-slate-500 text-sm mt-2">Perfecto para descubrir KAPITAL</p>
              </div>
              <ul className="space-y-3 mb-4">
                {[
                  { icon: '📊', text: '3 activos en cartera' },
                  { icon: '📈', text: 'Gráficos básicos' },
                  { icon: '⚡', text: 'Datos en tiempo real' },
                  { icon: '💼', text: 'Cartera visual' }
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-slate-400 group-hover:text-slate-300 transition-colors">
                    <span className="text-lg">{item.icon}</span>
                    <span>{item.text}</span>
                  </li>
                ))}
              </ul>
              {/* Lista completa de herramientas Gratis */}
              <div className="bg-slate-800/40 border border-slate-700/60 rounded-xl p-3 mb-4">
                <div className="flex items-center justify-between text-slate-400 text-xs font-bold uppercase tracking-wider mb-2">
                  <span>🆓 Incluye {planTools.free.length} herramientas</span>
                </div>
                <ul className="space-y-1.5 max-h-40 overflow-y-auto pr-1">
                  {planTools.free.map((t) => (
                    <li key={t.id} className="flex items-start gap-2 text-[12.5px] leading-snug text-slate-300">
                      <Check size={13} className="text-slate-400 shrink-0 mt-[3px]" strokeWidth={3} />
                      <span>{t.name}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <button 
                onClick={onGetStarted}
                className="w-full py-4 bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-slate-600 rounded-xl font-semibold transition-all text-slate-300 hover:text-white"
              >
                Empezar Gratis
              </button>
              <button
                onClick={() => handleDownloadPlanPDF('free')}
                className="w-full mt-2 py-2 flex items-center justify-center gap-1.5 text-slate-400 hover:text-slate-200 text-xs font-medium transition-colors"
                data-testid="plan-pdf-free"
                title="Descarga PDF con las herramientas del plan Gratis"
              >
                <FileDown size={13} /> Descargar detalles (PDF)
              </button>
            </div>

            {/* Pro - Highlighted */}
            <div className="group p-6 bg-gradient-to-b from-gold-500/20 via-gold-500/10 to-slate-900/80 border-2 border-gold-500/60 rounded-2xl relative transform hover:scale-[1.02] transition-all duration-300 shadow-[0_0_40px_rgba(201,162,39,0.18)]">
              <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-gold-300 to-gold-500 rounded-t-2xl" />
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-5 py-1.5 bg-gradient-to-r from-gold-500 to-gold-300 rounded-full text-sm font-bold text-slate-900 shadow-lg">
                ⭐ MÁS POPULAR
              </div>
              <div className="mb-6 mt-2">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 rounded-xl bg-gold-500/20 flex items-center justify-center border border-gold-500/30">
                    <span className="text-2xl">🚀</span>
                  </div>
                  <div>
                    <span className="text-gold-300 text-xs uppercase tracking-wider">Plan</span>
                    <div className="text-xl font-bold text-white">Pro</div>
                  </div>
                </div>
                <div className="flex items-baseline gap-2 mt-4">
                  <span className="text-5xl font-black text-white">19,99€</span>
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-emerald-400 text-sm font-semibold">✓ Pago único</span>
                  <span className="text-slate-500">·</span>
                  <span className="text-gold-300 text-sm font-semibold">Acceso de por vida</span>
                </div>
              </div>
              
              {/* Lista COMPLETA de herramientas PRO */}
              <div className="bg-gold-500/10 border border-gold-500/20 rounded-xl p-3 mb-4">
                <div className="flex items-center justify-between text-gold-300 text-xs font-bold uppercase tracking-wider mb-2">
                  <span>🛠️ PRO · {planTools.pro.length} herramientas incluidas</span>
                </div>
                <ul className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
                  {planTools.pro.map((t) => (
                    <li key={t.id} className="flex items-start gap-2 text-[12.5px] leading-snug text-slate-200">
                      <Check size={13} className="text-gold-300 shrink-0 mt-[3px]" strokeWidth={3} />
                      <span className="font-medium">{t.name}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              <ul className="space-y-2.5 mb-6">
                {[
                  { icon: '📊', text: '15 activos en cartera', highlight: true },
                  { icon: '🕯️', text: 'Gráficos de velas profesionales' },
                  { icon: '📉', text: 'Todos los indicadores técnicos' },
                  { icon: '🔔', text: 'Alertas de precio ilimitadas' },
                  { icon: '📰', text: 'Noticias del mercado en vivo' }
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-slate-300">
                    <span className="text-lg">{item.icon}</span>
                    <span className={item.highlight ? 'text-gold-300 font-medium' : ''}>{item.text}</span>
                  </li>
                ))}
              </ul>
              <button 
                onClick={onGetStarted}
                className="w-full py-4 bg-gradient-to-r from-gold-500 to-gold-300 hover:from-gold-300 hover:to-gold-200 text-slate-900 rounded-xl font-bold transition-all hover:shadow-[0_0_40px_rgba(201,162,39,0.5)] text-lg"
              >
                Obtener Pro →
              </button>
              <button
                onClick={() => handleDownloadPlanPDF('pro')}
                className="w-full mt-2 py-2 flex items-center justify-center gap-1.5 text-gold-300/80 hover:text-gold-200 text-xs font-medium transition-colors"
                data-testid="plan-pdf-pro"
                title="Descarga PDF con todas las herramientas del plan Pro"
              >
                <FileDown size={13} /> Descargar detalles (PDF)
              </button>
              <p className="text-center text-slate-500 text-xs mt-1">Ideal para traders activos</p>
            </div>

            {/* Ultimate - PREMIUM DESTACADO */}
            <div className="group p-6 bg-gradient-to-b from-gold-500/25 via-gold-500/10 to-slate-900/80 border-2 border-gold-500/70 rounded-2xl relative transform hover:scale-[1.02] transition-all duration-300 shadow-[0_0_60px_rgba(251,191,36,0.2)]">
              <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-gold-400 via-yellow-400 to-gold-500 rounded-t-2xl" />
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-5 py-1.5 bg-gradient-to-r from-gold-500 via-yellow-400 to-gold-500 rounded-full text-sm font-bold text-slate-900 shadow-lg flex items-center gap-2">
                <span>👑</span> MÁXIMO PODER
              </div>
              <div className="mb-6 mt-2">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-gold-500/30 to-yellow-500/20 flex items-center justify-center border border-gold-500/40">
                    <span className="text-2xl">💎</span>
                  </div>
                  <div>
                    <span className="text-gold-400 text-xs uppercase tracking-wider">Plan</span>
                    <div className="text-xl font-bold text-gold-300">Ultimate</div>
                  </div>
                </div>
                <div className="flex items-baseline gap-2 mt-4">
                  <span className="text-5xl font-black bg-gradient-to-r from-gold-300 to-yellow-400 bg-clip-text text-transparent">29,99€</span>
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-emerald-400 text-sm font-semibold">✓ Pago único</span>
                  <span className="text-slate-500">·</span>
                  <span className="text-gold-400 text-sm font-semibold">TODO incluido</span>
                </div>
              </div>
              
              {/* Lista COMPLETA de herramientas Ultimate */}
              <div className="bg-gold-500/10 border border-gold-500/20 rounded-xl p-3 mb-4">
                <div className="flex items-center justify-between text-gold-400 text-xs font-bold uppercase tracking-wider mb-2">
                  <span>🔥 ACCESO COMPLETO · {planTools.ultimate.length} herramientas</span>
                </div>
                <ul className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
                  {planTools.ultimate.map((t) => (
                    <li key={t.id} className="flex items-start gap-2 text-[12.5px] leading-snug text-slate-200">
                      <Check size={13} className="text-gold-400 shrink-0 mt-[3px]" strokeWidth={3} />
                      <span className="font-medium">{t.name}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              <ul className="space-y-2.5 mb-6">
                {[
                  { icon: '♾️', text: 'Activos ILIMITADOS', highlight: true },
                  { icon: '📈', text: 'Simulador de Inversiones (interés compuesto)' },
                  { icon: '🎯', text: 'Objetivos Financieros (metas de ahorro)' },
                  { icon: '🧪', text: 'Backtesting completo de estrategias' },
                  { icon: '📺', text: 'Multi-gráfico (4 activos a la vez)' },
                  { icon: '🤖', text: 'Análisis con IA avanzado' },
                  { icon: '📒', text: 'Contabilidad y registro automático' },
                  { icon: '⚡', text: 'Soporte prioritario VIP' }
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-slate-300">
                    <span className="text-lg">{item.icon}</span>
                    <span className={item.highlight ? 'text-gold-400 font-bold' : ''}>{item.text}</span>
                  </li>
                ))}
              </ul>
              <button 
                onClick={onGetStarted}
                className="w-full py-4 bg-gradient-to-r from-gold-500 via-yellow-400 to-gold-500 hover:from-gold-400 hover:via-yellow-300 hover:to-gold-400 text-slate-900 rounded-xl font-black transition-all hover:shadow-[0_0_50px_rgba(251,191,36,0.5)] text-lg relative overflow-hidden group"
              >
                <span className="relative z-10">🚀 Desbloquear Ultimate</span>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
              </button>
              <button
                onClick={() => handleDownloadPlanPDF('ultimate')}
                className="w-full mt-2 py-2 flex items-center justify-center gap-1.5 text-gold-300/80 hover:text-gold-200 text-xs font-medium transition-colors"
                data-testid="plan-pdf-ultimate"
                title="Descarga PDF con TODAS las herramientas del plan Ultimate"
              >
                <FileDown size={13} /> Descargar detalles (PDF)
              </button>
              <p className="text-center text-gold-400/70 text-xs mt-1 font-medium">La mejor inversión para tu trading</p>
            </div>
          </div>
          
          {/* Comparativa rápida */}
          <div className="mt-12 text-center">
            <p className="text-slate-500 text-sm">
              💡 <span className="text-slate-400">¿Indeciso?</span> El plan <span className="text-gold-400 font-semibold">Ultimate</span> incluye TODO lo del Pro + herramientas profesionales que normalmente cuestan <span className="line-through text-slate-600">+200€/año</span> en otras plataformas.
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 px-4 relative overflow-hidden">
        {/* Background effects */}
        <div className="absolute inset-0 bg-gradient-to-t from-gold-500/10 via-transparent to-transparent" />
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] rounded-full blur-[150px] opacity-20 bg-gold-500" />
        
        <div className="max-w-4xl mx-auto text-center relative">
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-black mb-6 leading-tight">
            ¿Listo para
            <br />
            <span className="gradient-text">dominar el mercado</span>?
          </h2>
          <p className="text-slate-400 mb-10 text-xl max-w-2xl mx-auto">
            Únete a miles de traders que ya usan KAPITAL para tomar mejores decisiones
          </p>
          <button
            onClick={onGetStarted}
            className="group relative h-16 px-12 bg-gradient-to-r from-gold-500 to-gold-300 rounded-2xl font-bold text-xl text-slate-900 hover:shadow-[0_0_60px_rgba(201,162,39,0.5)] transition-all duration-300 flex items-center justify-center gap-3 mx-auto overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
            <span className="relative">Crear Cuenta</span>
            <ArrowRight className="w-6 h-6 relative group-hover:translate-x-1 transition-transform" />
          </button>
          <p className="text-slate-500 mt-6 text-sm">Sin tarjeta de crédito para empezar</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 px-4 border-t border-white/5 bg-black/40">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-3">
            <img 
              src="/logo-kapital-k.png" 
              alt="Kapital" 
              className="h-14 w-14 object-contain" 
              style={{ filter: 'drop-shadow(0 0 10px rgba(255, 215, 0, 0.5))' }}
            />
            <div className="flex flex-col">
              <span className="text-xs text-zinc-400 tracking-wider">ANÁLISIS TÉCNICO</span>
              <span className="text-xl font-bold text-gold-400" style={{ marginTop: '-2px' }}>KAPITAL</span>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row items-center gap-4 text-slate-500 text-sm">
            <a href="mailto:kapital.analisistecnico@gmail.com" className="hover:text-gold-300 transition-colors">
              kapital.analisistecnico@gmail.com
            </a>
            <span className="hidden sm:inline text-slate-700">·</span>
            <span>© 2026 Analisis Tecnico KAPITAL</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
